import {ManuscriptDisclosure, ReportAiDisclosure} from '../../../components/ai/DisclosureControls';
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Media } from 'src/utils/Media';
import {useDispatch, useSelector} from '../../../reduxHooks';

import { useHistory, useLocation, useParams } from 'react-router-dom';

import queryString from 'query-string';

import { Alert, Button, Dialog } from '@betareader/design-system';
import AppIcon from '../../../components/AppIcon';
import { useTranslation } from 'react-i18next';

import { authUtil, sanitizeHtml } from '../../../utils';
import { updateJoinBetaRequest, sendJoinBetaRequest, fetchCurrentBook } from '../../../modules/book';
import { getUserProfile } from '../../../modules/user';
import { updateSearchTerm } from '../../../modules/betaOpportunities';

import JoinBetaForm from '../../discover/joinBetaForm';
import CurrentBetaIsFullMessage from '../../../components/standard-messages/currentBetaIsFull';
import { PaddedWrapper } from '../../../style';
import './viewBookDetails.css';

// The global mobile navigation is 62px tall before the device safe area.
export const MOBILE_NAVIGATION_CLEARANCE =
  'calc(62px + env(safe-area-inset-bottom))';

export const MobileBookActionBar = styled.div<{ $hasMobileNavigation: boolean }>`
  position: fixed;
  right: 0;
  bottom: ${({ $hasMobileNavigation }) =>
    $hasMobileNavigation
      ? MOBILE_NAVIGATION_CLEARANCE
      : 'env(safe-area-inset-bottom)'};
  left: 0;
  z-index: 149;
  box-sizing: border-box;
  padding: 6px 15px 8px;
  background: var(--br-surface);
  box-shadow: 0 -6px 16px rgba(13, 37, 40, 0.08);

  .br-button {
    min-height: 44px;
  }
  .br-book-action-group { display: flex; gap: var(--br-space-2); width: 100%; }
  .br-book-action-group > .br-button { flex: 1; }
`;

const DetailsForm = styled.div`
  display: grid;
  gap: var(--br-space-4);
  .br-book-action-group { display: flex; flex-wrap: wrap; gap: var(--br-space-2); }
  .br-book-section-heading { font-size: 0.875rem; margin: 0 0 var(--br-space-2); }
  .br-book-byline { color: var(--br-muted); display: flex; flex-wrap: wrap; gap: var(--br-space-2); }
  .br-book-genres { display: flex; flex-wrap: wrap; gap: var(--br-space-2); }
  .br-book-genre { background: var(--br-raised-surface); border: 1px solid var(--br-border); border-radius: var(--br-control-radius); color: var(--br-text); cursor: pointer; font: inherit; padding: var(--br-space-1) var(--br-space-2); }
  .br-book-genre:focus-visible { outline: 2px solid var(--br-focus); }
`;

type Props = {
  toggleFollowBook: (action: any) => Promise<void>;
};
const ViewBookDetails: React.FC<Props> = ({ toggleFollowBook }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const params = useParams<{ bookId: string }>();
  const dispatch = useDispatch();

  const user = useSelector(state => state.user.user);
  const userProfile = useSelector(state => state.user.userProfile);
  const isEmbedded = useSelector((state: any) => state.app.isEmbedded);
  const currentBook = useSelector(state => state.book.currentBook);

  const [token, setToken] = useState<string | undefined>();
  const prevReaderStatus = useRef<string | undefined>(undefined);

  // Ensure the MongoDB User document exists before any join request fires.
  // Handles the case where the user arrives via SPA redirect from signup and
  // onAuthStateChanged did not re-fire (so getUserProfile was never called).
  useEffect(() => {
    if (!user || userProfile) return;
    (async () => {
      const idToken = await authUtil.getFreshIdToken();
      if (idToken) dispatch(getUserProfile(idToken));
    })();
  }, [user?.uid]);

  useEffect(() => {
    const checkQuery = () => {
      const parsedQueryString = queryString.parse(location.search);
      const { show: parsedShow, token: parsedToken } = parsedQueryString;
      setToken(parsedToken);
    };
    checkQuery();
  }, [location]);

  // Send a join request directly without opening the modal (used when no survey/swap)
  const directJoinRequest = async () => {
    const idToken = await authUtil.getFreshIdToken();
    const options = {
      userId: user?.uid,
      token,
    };
    dispatch(sendJoinBetaRequest(idToken, params.bookId, options, () => {
      toggleFollowBook('follow');
    }));
  };

  // Auto-join when returning from sign-in/sign-up with ?join=1 in URL.
  // Reads the token directly from the URL rather than the token state to avoid
  // a timing issue where the token state effect may not have fired yet.
  useEffect(() => {
    const parsedQS = queryString.parse(location.search);
    if (parsedQS.join !== '1') return;
    // Wait until the user document has been persisted to MongoDB before sending
    // the join request — userProfile._id is only set after POST /profile succeeds.
    if (!user || !userProfile?._id || !currentBook || currentBook.reader) return;
    if ((currentBook.readerSignupSurvey?.questions?.length ?? 0) > 0) return;
    if (currentBook.swap) return;

    // Strip ?join=1 from URL (preserving any token param)
    const newQS = { ...parsedQS };
    delete newQS.join;
    const newSearch = queryString.stringify(newQS);
    history.replace(`${location.pathname}${newSearch ? '?' + newSearch : ''}`);

    // Fire the join request automatically
    (async () => {
      const idToken = await authUtil.getFreshIdToken();
      if (!idToken) return;
      dispatch(sendJoinBetaRequest(
        idToken,
        params.bookId,
        { userId: user?.uid, token: parsedQS.token as string | undefined },
        () => { toggleFollowBook('follow'); }
      ));
    })();
  }, [user?.uid, userProfile?._id, currentBook?._id, currentBook?.reader]);

  // Poll for approval while the reader's request is pending
  useEffect(() => {
    const readerStatus = currentBook?.reader?.invitation?.status;
    if (readerStatus !== 'REQUESTED') return;

    const pollBook = async () => {
      const idToken = await authUtil.getFreshIdToken();
      if (idToken) {
        dispatch(fetchCurrentBook({ idToken, bookId: params.bookId, token }));
      }
    };

    const intervalId = setInterval(pollBook, 30000);
    return () => clearInterval(intervalId);
  }, [currentBook?.reader?.invitation?.status, params.bookId, token]);

  // Auto-navigate to the reader when approval arrives while the user is waiting.
  // Only fires on a REQUESTED → APPROVED transition, not if already APPROVED on load.
  useEffect(() => {
    const status = currentBook?.reader?.invitation?.status;
    const prev = prevReaderStatus.current;
    prevReaderStatus.current = status;
    if (prev === 'REQUESTED' && status === 'APPROVED' && user) {
      acceptInvitationAndGoToBook();
    }
  }, [currentBook?.reader?.invitation?.status]);

  const goToSwapRequest = () => {
    if (currentBook.swap) {
      history.push(`/books/${currentBook.swap._id}/readers`);
    }
  };

  const doShowJoinBetaForm = () => {
    if (!user) {
      const noSurvey = !(currentBook?.readerSignupSurvey?.questions?.length);
      const noSwap = !currentBook?.swap;
      if (noSurvey && noSwap) {
        // Preserve any existing params (e.g. invitation token) and add join=1
        // so the book page can auto-trigger the join request after sign-in/sign-up
        const newQS = queryString.parse(location.search);
        newQS.join = '1';
        history.push(`/signin?redirect=${encodeURIComponent(
          `${location.pathname}?${queryString.stringify(newQS)}`
        )}`);
      } else {
        history.push(`/signin?redirect=${encodeURIComponent(
          location.pathname + location.search
        )}`);
      }
    } else {
      const queryStringObject = queryString.parse(location.search);
      queryStringObject.show = 'join';
      history.push(
        `${location.pathname}?${queryString.stringify(queryStringObject)}`
      );
    }
  };

  const closeJoinBetaForm = () => {
    const queryStringObject = queryString.parse(location.search);
    queryStringObject.show = '';

    history.push(
      `${location.pathname}?${queryString.stringify(queryStringObject)}`
    );
  };

  const goToBook = () => {
    const bookURL = `/read?book=${params.bookId}`;
    history.push(bookURL);
  };

  const declineInvitation = async () => {
    const idToken = await authUtil.getFreshIdToken();
    const options = {
      status: 'DECLINED',
      token,
    };
    if (!user) {
      window.localStorage.setItem('br-invitation-declined', token as string);
      goToSignIn();
    } else {
      dispatch(
        updateJoinBetaRequest(idToken, params.bookId, options, (succeeded: any) => {
          console.log('succeeded', succeeded);
        })
      );
    }
  };

  const acceptInvitationAndGoToBook = async () => {
    const idToken = await authUtil.getFreshIdToken();
    const options = {
      status: 'ACCEPTED',
      token,
    };
    if (!user) {
      if (token) window.localStorage.setItem('br-invitation-accepted', token);
      goToSignIn();
    } else {
      dispatch(
        updateJoinBetaRequest(idToken, params.bookId, options, (succeeded: any) => {
          if (succeeded) {
            setTimeout(() => {
              toggleFollowBook('follow');
              goToBook();
            }, 1000);
          }
        })
      );
    }
  };

  const goToSignUp = () => {
    history.push(
      `/signin?redirect=${encodeURIComponent(
        location.pathname + location.search
      )}`
    );
  };

  const goToSignIn = () => {
    history.push(
      `/signin?redirect=${encodeURIComponent(
        location.pathname + location.search
      )}`
    );
  };

  const goToDiscover = () => {
    history.push('/discover');
  };

  const selectGenre = (genre: any) => {
    dispatch(updateSearchTerm(''));
    history.push(`/discover/genre/${genre}`);
  };

  const isAuthor = () => {
    const isAuthor =
      userProfile &&
      currentBook.author &&
      currentBook.author._id === userProfile._id;
    return isAuthor;
  };

  // rendering
  const queryStringObject = queryString.parse(location.search);
  const { show } = queryStringObject;

  if (!currentBook) {
    return null;
  }

  const invitationPending =
    currentBook.reader &&
    ['SENT', 'CREATED', 'DECLINED'].includes(
      currentBook.reader.invitation.status
    );

  const hasSurvey = (currentBook.readerSignupSurvey?.questions?.length ?? 0) > 0;

  let joinButtonText = 'JoinBeta';
  // Default: open modal. For no-survey/no-swap authenticated new readers, go direct.
  let onClickFn: (() => void | Promise<void>) | undefined = (!currentBook.reader && !currentBook.swap && !hasSurvey && user)
    ? directJoinRequest
    : doShowJoinBetaForm;

  if (currentBook.swap) {
    joinButtonText = 'GoToSwapRequest';
    onClickFn = goToSwapRequest;
  }
  if (currentBook.reader) {
    switch (currentBook.reader.invitation.status) {
      case 'CREATED':
      case 'SENT':
      case 'DECLINED':
        joinButtonText = 'Accept';
        if (!hasSurvey) {
          onClickFn = acceptInvitationAndGoToBook;
        }
        break;
      case 'ACCEPTED':
        joinButtonText = 'Read';
        onClickFn = goToBook;
        break;
      case 'APPROVED':
        joinButtonText = 'Read';
        onClickFn = acceptInvitationAndGoToBook;
        break;
      case 'REQUESTED':
        joinButtonText = 'Requested';
        onClickFn = undefined;
        break;
      case 'ABANDONED':
        joinButtonText = 'Abandoned';
        break;
      default:
    }
  }
  const declineButton = (
    <Button
      disabled={
        currentBook.reader &&
        currentBook.reader.invitation.status === 'DECLINED'
      }
      key="declineInvitationButtonKey"
      onClick={declineInvitation}
      variant="secondary"
    >{t('Decline')}</Button>
  );

  const joinButton = (
    <Button
      key="joinButtonKey"
      id="joinbutton"
      aria-busy={currentBook.state === 'sending-read-request'}
      disabled={
        currentBook.state === 'sending-read-request' ||
        (currentBook.reader &&
          ['ABANDONED', 'REQUESTED'].includes(currentBook.reader.invitation.status)) ||
        (currentBook.currentBetaIsFull && currentBook.reader)
      }
      className="primary-br-button"
      onClick={onClickFn}
    >{t(joinButtonText)}</Button>
  );

  return (
    <PaddedWrapper>
      <DetailsForm>
        <div>
          <h1 className="br-text">{currentBook.title}</h1>
          <div className="br-book-byline br-text">
              {`${t('by')} ${currentBook.authorName}`}
              {currentBook.isPremium && (
                <span title={t('PremiumAuthorInfo')} aria-label={t('PremiumAuthorInfo')}><AppIcon name="star" aria-hidden="true" /></span>
              )}
              {currentBook.pubDate && (
                <div style={{ float: 'right', display: 'inline-block' }}>
                  <span>
                    {t('PublicationDate')}{' '}
                    {moment(currentBook.pubDate).format('L')}
                  </span>
                </div>
              )}
          </div>
        </div>
        <ManuscriptDisclosure book={currentBook} details />
        <Media greaterThanOrEqual="tablet">
          {(className, renderChildren) =>
            renderChildren && (
              <div>
                <div className="br-book-action-group">
                  {invitationPending && declineButton}
                  {joinButton}
                </div>
              </div>
            )
          }
        </Media>
        {currentBook.swap &&
          currentBook.swap.invitation &&
          currentBook.swap.invitation.status &&
          ['REQUESTED', 'APPROVED', 'ACCEPTED'].includes(
            currentBook.swap.invitation.status
          ) && (
            <div>
              <Alert variant="info">
                <em style={{ fontSize: '0.8em', opacity: 0.6 }}>
                  {t('AuthorXHasProposedToSwapThisForY', {
                    x: currentBook.authorName,
                    y: currentBook.swap.title,
                  })}
                </em>
              </Alert>
              {currentBook.currentBetaIsFull && (
                <CurrentBetaIsFullMessage key="CurrentBetaIsFullMessage" />
              )}
            </div>
          )}
        {currentBook.reader &&
          currentBook.reader.invitation.status === 'REQUESTED' && (
            <div>
              <Alert variant="success">
                {currentBook.linkedJoinRequest
                  ? t('RequestedWithDifferentEmail')
                  : t('AnsweredOrRequestedNotice')}
              </Alert>
            </div>
          )}
        {invitationPending &&
          !currentBook.swap &&
          currentBook.reader.invitation.status !== 'DECLINED' && (
            <div>
              <Alert variant="info">
                {t('YouHaveAPendingInvitation')}
              </Alert>
              {currentBook.currentBetaIsFull && (
                <CurrentBetaIsFullMessage key="CurrentBetaIsFullMessageInvitation" />
              )}
            </div>
          )}
        {invitationPending &&
          !currentBook.swap &&
          currentBook.reader.invitation.status === 'DECLINED' && (
            <div>
              <Alert variant="info">
                {t('DeclinedInvitationMessage')}
              </Alert>
              {currentBook.currentBetaIsFull && (
                <CurrentBetaIsFullMessage key="CurrentBetaIsFullMessageInvitation" />
              )}
            </div>
          )}
        <div className="br-book-genres">
          {currentBook.genres &&
            currentBook.genres.map((genre: any, index: any) =>
              index >= 0 ? (
                <button type="button"
                  className="br-book-genre"
                  onClick={() => selectGenre(genre)}
                  key={`${index + genre}`}
                >{genre}</button>
              ) : null
            )}
        </div>
        <div>
          <h2 className="br-text br-book-section-heading">{t('Description')}</h2>
          <ReportAiDisclosure bookId={currentBook._id} feedbackId={undefined} />
          <div
            id="description"
            className="br-text"
            dangerouslySetInnerHTML={{
              __html: currentBook.description
                ? sanitizeHtml(currentBook.description.replace(/\n/g, '<br />'))
                : t('OpportunityWithoutDescriptionMessage'),
            }}
          />
        </div>
        <div>
          <h2 className="br-text br-book-section-heading">{t('FeedbackGuidelines')}</h2>
          <div
            id="feedback-guidelines"
            className="br-text"
            dangerouslySetInnerHTML={{
              __html: currentBook.feedbackGuidelines
                ? sanitizeHtml(currentBook.feedbackGuidelines.replace(/\n/g, '<br />'))
                : t('NoGuidelines'),
            }}
          />
        </div>
        <div>
          <h2 className="br-text br-book-section-heading">{t('CopyrightInfo')}</h2>
          {currentBook.copyRightInfo && (
            <div
              id="copyright"
              className="br-text"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(currentBook.copyRightInfo.replace(/\n/g, '<br />')),
              }}
            />
          )}
        </div>
      </DetailsForm>
      <Media at="mobile">
        {(className, renderChildren) =>
          renderChildren && (
            <MobileBookActionBar $hasMobileNavigation={!!userProfile && !isEmbedded}>
              <div className="br-book-action-group">
                {invitationPending && declineButton}
                {joinButton}
              </div>
            </MobileBookActionBar>
          )
        }
      </Media>
      <Media at="mobile">
        {(className, renderChildren) =>
          renderChildren && <div style={{ height: '76px' }} aria-hidden="true" />
        }
      </Media>
      <Dialog
        open={show === 'join'}
        title={t('JoinBeta')}
        onClose={closeJoinBetaForm}
        actions={<Button variant="secondary" onClick={closeJoinBetaForm}>{t('Close')}</Button>}
        className="br-book-join-dialog"
      >
        <JoinBetaForm
          onClose={closeJoinBetaForm}
          onJoin={() => toggleFollowBook('follow')}
          onWithdraw={() => toggleFollowBook('unfollow')}
        />
      </Dialog>
    </PaddedWrapper>
  );
};

export default ViewBookDetails;
